import { CommonModule } from '@angular/common';
import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';

import { TranslocoModule } from '@jsverse/transloco';
import { merge } from 'lodash';

import { DialogOptions } from '../../../interfaces';
import { InputSlideToggleComponent } from '../../fields';
import { DialogBaseComponent } from '../dialog-base/dialog-base.component';

@Component({
  selector: 'hmax-dialog-options',
  standalone: true,
  templateUrl: './dialog-options.component.html',
  styleUrls: ['./dialog-options.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule, DialogBaseComponent, InputSlideToggleComponent, MatDialogModule, TranslocoModule],
})
export class DialogOptionsComponent {
  private data = inject<DialogOptions>(MAT_DIALOG_DATA);
  private _dialogRef = inject(MatDialogRef<DialogOptionsComponent>);
  private _formBuilder = inject(FormBuilder);

  form: FormGroup = this._formBuilder.group({});
  optionsPosition!: 'text-start' | 'text-end' | 'text-center';
  optionsPositionLabel!: 'before' | 'after';
  hasOptions = false;

  constructor() {
    if (this.data?.options) {
      const options = this.data.options;
      this.hasOptions = true;
      this.optionsPosition = this.data?.optionsPosition ?? 'text-start';
      this.optionsPositionLabel = this.data?.optionsPositionLabel ?? 'after';

      for (const option in this.data.options) {
        if (Object.prototype.hasOwnProperty.call(options, option)) {
          const name = `${option}`;

          this.form.addControl(name, new FormControl(options[option]));
        }
      }
    }
  }

  /**
   * Abrir um modal deste componente com as configurações padrão
   * Precisa receber uma instância do MatDialog
   * ex: DialogOptionsComponent.open(this._matDialog);
   * @param matDialog Instância de MatDialog do Angular Material que deve ser injetada
   * pela classe que invocar este método
   */
  static open(matDialog: MatDialog, data?: DialogOptions): MatDialogRef<DialogOptionsComponent> {
    const defaultConfig: DialogOptions = {
      actions: {
        confirm: {
          color: 'primary',
          label: 'save',
          labelTranslate: true,
          show: true,
          action: () => dialogRef.componentInstance.save(),
        },
        cancel: {
          label: 'cancel',
          labelTranslate: true,
          show: true,
        },
      },
    };

    if (data?.actions?.buttons) {
      data.actions.buttons?.forEach((item) => (item.action = () => dialogRef.componentInstance.saveAction(item?.label)));
    }

    const config = merge({}, defaultConfig, data);

    const dialogRef = matDialog.open(DialogOptionsComponent, {
      autoFocus: false,
      data: { matDialog, ...config },
      disableClose: false,
    });

    return dialogRef;
  }

  save(): void {
    this._dialogRef.close(this.form?.value);
  }

  saveAction(label?: string): void {
    this._dialogRef.close({ ...this.form?.value, action: label });
  }
}
